import { useState, useEffect } from 'react';
import { RefreshCw, ShieldCheck, Users, TrendingUp, Database, Download, CheckCircle2, X, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PenaltyRecoveryTab = ({ sessionToken }) => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [eligFilter, setEligFilter] = useState('');
  const [marking, setMarking] = useState(false);
  const [purging, setPurging] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selected, setSelected] = useState(null);

  const H = { Authorization: `Bearer ${sessionToken}` };

  const fetchAll = async () => {
    if (!sessionToken) return;
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (eligFilter) params.set('eligible', eligFilter);
      const [statsRes, listRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/v1/admin/penalty-recovery/stats`, { headers: H }),
        fetch(`${BACKEND_URL}/api/v1/admin/penalty-recovery/list?limit=150&${params.toString()}`, { headers: H }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (listRes.ok) setLeads((await listRes.json()).leads || []);
    } catch (e) {
      console.error(e);
      toast.error('Error loading Penalty Recovery data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [sessionToken, statusFilter, eligFilter]);

  const handleExportCsv = async () => {
    if (!sessionToken || exporting) return;
    setExporting(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const r = await fetch(`${BACKEND_URL}/api/v1/admin/penalty-recovery/export.csv${qs}`, { headers: H });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const count = r.headers.get('x-record-count') || '?';
      const fileUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = `penalty_recovery_leads_${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(fileUrl), 1500);
      toast.success(`Exported ${count} leads`);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  const handleMarkPulled = async () => {
    if (marking) return;
    setMarking(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/v1/admin/crm/bridge/mark-pulled/penalty_recovery`, { method: 'POST', headers: H });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Marked ${d.marked} bridge lead(s) as pulled by app.kgob.com`);
      fetchAll();
    } catch (e) {
      toast.error(`Cleanup failed: ${e.message || e}`);
    } finally {
      setMarking(false);
    }
  };

  const handleDelete = async (lead, e) => {
    if (e) e.stopPropagation();
    if (deletingId) return;
    if (!window.confirm(`Delete this penalty-recovery lead permanently?\n\nThis removes it from kgob.com and the shared bridge. app.kgob.com keeps its own copy.`)) return;
    setDeletingId(lead.id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/v1/admin/penalty-recovery/${lead.id}`, { method: 'DELETE', headers: H });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Lead deleted (bridge: ${d.deleted_bridge} removed)`);
      setSelected(null);
      fetchAll();
    } catch (err) {
      toast.error(`Delete failed: ${err.message || err}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePurgeBridge = async () => {
    if (purging) return;
    const n = stats?.bridge_status?.total_in_bridge || 0;
    if (!window.confirm(`Purge ALL ${n} penalty-recovery lead(s) from the shared bridge?\n\nThis deletes every bridge doc in penalty_recovery_leads. Your kgob.com DB and app.kgob.com's copy are NOT affected.`)) return;
    setPurging(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/v1/admin/crm/bridge/purge/penalty_recovery`, { method: 'POST', headers: H });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Purged ${d.deleted} lead(s) from the bridge`);
      fetchAll();
    } catch (e) {
      toast.error(`Purge failed: ${e.message || e}`);
    } finally {
      setPurging(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-300" data-testid="penalty-recovery-tab-loading">Loading Penalty Recovery data…</div>;
  }

  const bs = stats?.bridge_status || {};

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="penalty-recovery-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-ivory flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
            COVID Penalty Recovery
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Eligibility leads from <code className="text-[#D4AF37]">/penalty-recovery</code>. Dual-write to main DB + bridge; app.kgob.com runs the $295 review.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={fetchAll} disabled={refreshing} className="flex items-center gap-2 px-3 py-2 bg-navy-light hover:bg-navy text-ivory text-sm rounded-md disabled:opacity-50" data-testid="penalty-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={handleExportCsv} disabled={exporting} className="flex items-center gap-2 px-3 py-2 bg-[#D4AF37] hover:bg-[#b89530] text-navy text-sm font-semibold rounded-md disabled:opacity-50" data-testid="penalty-export-csv">
            <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Total leads" value={stats.total} icon={Users} />
          <MetricCard label="Last 24 hours" value={stats.last_24h} icon={TrendingUp} />
          <MetricCard label="Last 7 days" value={stats.last_7d} icon={TrendingUp} />
          <MetricCard label="Paid ($295)" value={stats.by_status.checkout_complete} icon={CheckCircle2} />
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-navy-light/40 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-ivory mb-3">Funnel & eligibility</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="Handover (free check)" value={stats.by_status.handover} />
              <Stat label="Checkout complete" value={stats.by_status.checkout_complete} />
              <Stat label="Callback requests (closed)" value={stats.by_status.closed_waitlist ?? 0} />
              <Stat label="Eligible" value={stats.by_eligibility.eligible} tone="green" />
              <Stat label="Not eligible" value={stats.by_eligibility.not_eligible} tone="red" />
              <Stat label="Unknown" value={stats.by_eligibility.unknown} />
              <Stat label="Left an email" value={stats.with_email} />
            </div>
          </div>
          <div className="bg-navy-light/40 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-ivory mb-3 flex items-center gap-2">
              <Database className="w-4 h-4 text-[#D4AF37]" /> CRM Bridge (app.kgob.com)
            </h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${bs.connected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-gray-300">{bs.connected ? 'Bridge connected' : 'Bridge disconnected'}</span>
              </div>
              <div className="text-gray-300">Total in bridge: <span className="font-semibold text-ivory">{bs.total_in_bridge}</span></div>
              <div className="text-gray-300">
                Not yet pulled by app.kgob.com:{' '}
                <span className={`font-semibold ${bs.not_pulled_by_app_kgob > 0 ? 'text-amber-400' : 'text-green-400'}`}>{bs.not_pulled_by_app_kgob}</span>
              </div>
              <button
                onClick={handleMarkPulled}
                disabled={marking || !bs.connected || bs.not_pulled_by_app_kgob === 0}
                className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-navy-light hover:bg-navy border border-gray-600 text-ivory text-xs rounded-md disabled:opacity-40"
                data-testid="penalty-mark-pulled"
                title="app.kgob.com mirrors penalty leads via direct bridge read; this marks them pulled (non-destructive)."
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {marking ? 'Marking…' : `Mark ${bs.not_pulled_by_app_kgob || 0} as pulled`}
              </button>
              <button
                onClick={handlePurgeBridge}
                disabled={purging || !bs.connected || bs.total_in_bridge === 0}
                className="mt-2 ml-2 flex items-center gap-2 px-3 py-1.5 bg-red-900/30 hover:bg-red-900/50 border border-red-700/50 text-red-300 text-xs rounded-md disabled:opacity-40"
                data-testid="penalty-purge-bridge"
                title="Delete ALL penalty-recovery docs from the shared bridge. Your DB and app.kgob.com's copy are unaffected."
              >
                <Trash2 className="w-3.5 h-3.5" />
                {purging ? 'Purging…' : `Purge bridge (${bs.total_in_bridge || 0})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-navy-light/40 border border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-ivory">Leads ({leads.length})</h3>
          <div className="flex items-center gap-2" data-testid="penalty-filters">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs bg-navy-light border border-gray-700 rounded px-2 py-1 text-ivory" data-testid="penalty-status-filter">
              <option value="">All statuses</option>
              <option value="handover">Handover</option>
              <option value="checkout_complete">Checkout complete</option>
              <option value="closed_waitlist">Callback request</option>
            </select>
            <select value={eligFilter} onChange={(e) => setEligFilter(e.target.value)} className="text-xs bg-navy-light border border-gray-700 rounded px-2 py-1 text-ivory" data-testid="penalty-elig-filter">
              <option value="">All eligibility</option>
              <option value="yes">Eligible</option>
              <option value="no">Not eligible</option>
            </select>
          </div>
        </div>
        {leads.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No penalty-recovery leads match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="penalty-leads-table">
              <thead className="bg-navy/50 text-gray-400 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-2">When</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Eligible</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Location</th>
                  <th className="text-left px-4 py-2">CRM</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="text-ivory">
                {leads.map((l) => {
                  const geo = l.geolocation || {};
                  const loc = [geo.city, geo.region].filter(Boolean).join(', ') || '—';
                  return (
                    <tr key={l.id} onClick={() => setSelected(l)} className="border-t border-gray-800 hover:bg-navy/30 cursor-pointer" data-testid={`penalty-lead-row-${l.id}`}>
                      <td className="px-4 py-2 text-gray-400 whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${l.status === 'checkout_complete' ? 'bg-green-500/20 text-green-300' : l.status === 'closed_waitlist' ? 'bg-amber-500/20 text-amber-300' : 'bg-blue-500/20 text-blue-300'}`}>
                          {l.status === 'checkout_complete' ? 'Paid' : l.status === 'closed_waitlist' ? 'Callback' : 'Handover'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {l.eligible === true ? <span className="text-green-400">Yes</span> : l.eligible === false ? <span className="text-red-400">No</span> : <span className="text-gray-500">—</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-300 text-xs">
                        <div>{l.email || '—'}</div>
                        {(l.name || l.phone) && <div className="text-gray-500">{[l.name, l.phone].filter(Boolean).join(' · ')}</div>}
                      </td>
                      <td className="px-4 py-2 text-gray-400 text-xs">{loc}</td>
                      <td className="px-4 py-2 text-xs">
                        <span className={l.crm_sync_status === 'synced' ? 'text-green-400' : l.crm_sync_status === 'skipped_no_receiver' ? 'text-amber-400' : 'text-gray-400'}>
                          {l.crm_sync_status || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelected(l); }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-[#D4AF37] hover:bg-[#D4AF37]/10"
                          data-testid={`penalty-view-${l.id}`}
                          title="View full lead data"
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        <button
                          onClick={(e) => handleDelete(l, e)}
                          disabled={deletingId === l.id}
                          className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded text-gray-500 hover:text-red-400 hover:bg-red-900/20 disabled:opacity-40"
                          data-testid={`penalty-delete-${l.id}`}
                          title="Delete lead (from kgob.com + bridge)"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <DetailDrawer lead={selected} onClose={() => setSelected(null)} onDelete={handleDelete} deleting={deletingId === selected.id} />}
    </div>
  );
};

const DetailDrawer = ({ lead, onClose, onDelete, deleting }) => {
  const geo = lead.geolocation || {};
  const ans = lead.answers || {};
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="penalty-detail-drawer">
      <div data-testid="penalty-recovery-tab-div-1" className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-navy border-l border-gray-700 overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-ivory">Penalty Recovery lead</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-ivory" data-testid="penalty-detail-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <Field label="Session ID" value={lead.session_id} mono />
          <Field label="Status" value={lead.status === 'checkout_complete' ? 'Checkout complete (paid)' : 'Handover (free check)'} />
          <Field label="Eligible" value={lead.eligible === true ? 'Yes' : lead.eligible === false ? 'No' : 'Unknown'} />
          <Field label="Email" value={lead.email || '—'} />
          <Field label="Wants summary" value={String(lead.wants_summary ?? '—')} />
          <Field label="Last CTA target" value={lead.last_cta_target || '—'} />
          <Field label="Created" value={lead.created_at ? new Date(lead.created_at).toLocaleString() : '—'} />
          <Field label="CRM sync" value={lead.crm_sync_status || '—'} />
          <Field label="Location" value={[geo.city, geo.region, geo.country_code || geo.country].filter(Boolean).join(', ') || '—'} />
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Free-check answers</div>
            <pre className="bg-navy-light/50 border border-gray-700 rounded p-2 text-xs text-gray-300 overflow-x-auto">{Object.keys(ans).length ? JSON.stringify(ans, null, 2) : '— (no free check)'}</pre>
          </div>
          {Array.isArray(lead.cta_events) && lead.cta_events.length > 0 && (
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">CTA events ({lead.cta_events.length})</div>
              <pre className="bg-navy-light/50 border border-gray-700 rounded p-2 text-xs text-gray-300 overflow-x-auto">{JSON.stringify(lead.cta_events, null, 2)}</pre>
            </div>
          )}
          <button
            onClick={(e) => onDelete(lead, e)}
            disabled={deleting}
            className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-red-900/30 hover:bg-red-900/50 border border-red-700/50 text-red-300 text-sm rounded-md disabled:opacity-40"
            data-testid="penalty-detail-delete"
          >
            <Trash2 className="w-4 h-4" /> {deleting ? 'Deleting…' : 'Delete this lead'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
    <div className={`text-ivory ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</div>
  </div>
);

const Stat = ({ label, value, tone }) => (
  <div className="bg-navy/50 rounded px-3 py-2">
    <div className="text-xs text-gray-400">{label}</div>
    <div className={`text-xl font-semibold ${tone === 'green' ? 'text-green-400' : tone === 'red' ? 'text-red-400' : 'text-ivory'}`}>{value}</div>
  </div>
);

const MetricCard = ({ label, value, icon: Icon }) => (
  <div className="bg-navy-light/40 border border-gray-700 rounded-lg px-4 py-3">
    <div className="flex items-center justify-between mb-1">
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" />}
    </div>
    <div className="text-2xl font-semibold text-ivory">{value}</div>
  </div>
);

export default PenaltyRecoveryTab;
